import React, { useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Button, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import LightIcon from '@mui/icons-material/LightModeOutlined';
import DarkIcon from '@mui/icons-material/DarkModeOutlined';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import { useTranslation } from 'react-i18next';

import { CODIGOS_33, FAMILIAS } from '../config/ensenanzas33';
import { colorRayo, colorIdentidad } from '../config/rayos';
import { formatoTitulo } from '../config/titulos';
import { nombreVoz } from '../config/nombres_voz';
import { planLabelKey } from '../config/lector';
import { useCatalog, useTeaching } from '../content/hooks';
import { pieceBlocks, resolveBlocks } from '../content/blocks';
import PieceBlocks, { InlineRuns } from '../components/PieceBlocks';
import { useModo } from '../config/modo';
import AvisoIdioma from '../components/AvisoIdioma';
import LuzDeLaVoz from '../components/LuzDeLaVoz';
import './canalizacion.css';
import './biblioteca.css';
import './treintaytres.css';

/**
 * UNA DE LAS 33 ENSEÑANZAS PARA LA HUMANIDAD — `/33/<n>` (Def 93 · Def 95).
 *
 * Def 93 (Faina, 14-sep): «hoy cada enseñanza abre la Biblioteca filtrada;
 * cuando tenga su texto propio (la gran enseñanza de Faina, Def 62) abre su
 * página /33/<n>». Esta es esa página. Debajo del texto van sus
 * canalizaciones: las publicadas que llevan el código de la enseñanza.
 *
 * Def 95 (Faina, 15-sep): las 33 hablan a la HUMANIDAD, en plural, con la voz
 * de Los Esenios en nombre de esenios, ángeles y arcángeles. Por eso aquí no
 * hay personalización: ni {NOMBRE} ni género. Es la única excepción a la Def 40
 * y el texto se enseña tal cual viene del Word.
 *
 * El texto sale de `GET /content/ensenanza-<n>`; el servidor decide cuánto
 * entrega según el plan.
 *
 * 🔴 DEF 73 rige igual que en una canalización: sin registro se ve el primer
 * párrafo y la puerta. Una enseñanza es contenido de la Fundación, no una
 * portada.
 */
/* Los seis textos propios de esta pagina (t33.p.*) viven en la tabla de
   traduccion desde el 21-sep, en los 55 idiomas; el valor por defecto en
   español que los cubria mientras tanto se retiro (la tabla manda). */
const EnsenanzaPage = () => {
  const { t } = useTranslation();
  const { n } = useParams();
  const numero = parseInt(n, 10);
  const { items: catalogo } = useCatalog();
  const { modo, esOscuro, alternar, alineacion, tam, agrandar, achicar } = useModo();

  const { teaching: ensenanza, loading } = useTeaching(numero);
  const blocks = useMemo(() => (ensenanza ? resolveBlocks(pieceBlocks(ensenanza), (text) => text) : []), [ensenanza]);
  const codigo = CODIGOS_33[numero - 1] || null;
  const familia = codigo ? FAMILIAS.find((f) => f.codigos.includes(codigo)) : null;

  /* Sus canalizaciones: las publicadas que llevan este código, en el orden del
     catálogo. Se cuentan, no se escriben (regla de la verdad). */
  const suyas = useMemo(
    () => (codigo ? catalogo.filter((c) => (c.ensenanzas || []).includes(codigo)) : []),
    [codigo, catalogo]
  );

  useEffect(() => { window.scrollTo(0, 0); }, [n]);

  const entidad = ensenanza && ensenanza.entidad ? ensenanza.entidad : 'Los Esenios';
  const rayo = colorRayo(entidad, modo);
  const puedeLeer = Boolean(ensenanza) && !ensenanza.bloqueada;
  const planRequerido = ensenanza && ensenanza.requiredPlan ? t(planLabelKey(ensenanza.requiredPlan)) : null;

  if (!codigo) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 3 }}>{t('t33.p.no_esta')}</Typography>
        <Button variant="outlined" component={RouterLink} to="/33">{t('t33.p.volver')}</Button>
      </Container>
    );
  }

  return (
    <>
      <LuzDeLaVoz entidad={entidad} modo={modo} />

      <Container component="article" className="e33" sx={{ maxWidth: '46rem !important', pb: 8 }}>
        <Box className="no-imprimir" sx={{ pb: 1, mt: -1 }}>
          <Button
            component={RouterLink}
            to="/33"
            startIcon={<ArrowBackIcon sx={{ fontSize: '.8rem' }} />}
            sx={{ color: 'var(--texto-tenue)', fontSize: '.75rem', px: 0, '&:hover': { color: 'var(--texto)', bgcolor: 'transparent' } }}
          >
            {t('t33.p.volver')}
          </Button>
        </Box>

        {/* El número, como en la página 33: es el 1…33 de la enseñanza, no el código. */}
        <Typography component="p" sx={{
          textAlign: 'center', fontFamily: 'var(--fuente-titulo)', fontSize: '.78rem',
          letterSpacing: '.18em', textTransform: 'uppercase', color: rayo, mb: 1
        }}>
          {t('t33.p.de_33', { n: numero })}
        </Typography>

        <Typography component="h1" sx={{
          fontFamily: 'var(--fuente-lectura)', fontWeight: 300,
          fontSize: 'clamp(2rem, 5.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em',
          textAlign: 'center', mb: 2
        }}>
          {formatoTitulo(ensenanza && ensenanza.titulo ? ensenanza.titulo : t(`t33.e.${codigo}`))}
        </Typography>

        <Typography component="p" className="ficha-corta" sx={{
          textAlign: 'center', fontFamily: 'var(--fuente-titulo)', fontSize: '.82rem',
          color: 'var(--texto-tenue)', letterSpacing: '.04em', mb: 5
        }}>
          {familia ? t(familia.clave) : ''}
          {ensenanza && (<>
            {' · '}{nombreVoz(entidad, t)}
            {' · '}{t('ficha.canalizado_por')}{' '}
            <Box component={RouterLink} to="/nosotros#como-trabajamos"
                 sx={{ color: 'var(--acento)', fontWeight: 500, textDecoration: 'none',
                       borderBottom: '1px solid color-mix(in srgb, var(--acento) 45%, transparent)',
                       '&:hover': { borderBottomColor: 'var(--acento)' } }}>
              {ensenanza.canalizador}
            </Box>
          </>)}
        </Typography>

        {/* Todavía sin texto: la enseñanza existe como código y como puerta a sus
            canalizaciones (Def 93, primer estado). Se dice, no se disimula. */}
        {!ensenanza && !loading && (
          <Typography component="p" sx={{ textAlign: 'center', color: 'var(--texto-tenue)', mb: 5 }}>
            {t('t33.p.sin_texto')}
          </Typography>
        )}

        {ensenanza && (<>
          <AvisoIdioma />
          {!puedeLeer && (
            <Box className="cerrada">
              <Typography className={`cuerpo-canalizacion alin-${alineacion} parrafo primero cerrada-asomo`}
                          sx={{ '--rayo': rayo, '--tam': `${tam}rem` }}>
                {blocks[0] && <InlineRuns runs={blocks[0].items[0]} />}
              </Typography>
              <Box className="cerrada-aviso" sx={{ '--rayo': rayo }}>
                <span className="cerrada-luz" aria-hidden="true" />
                {/* Def 101: las 33 enseñanzas son del plan 3; la puerta lleva a los planes. */}
                <RouterLink to="/#planes" className="cerrada-boton">
                  {t('cuenta.ver_planes')}
                </RouterLink>
                <Typography className="cerrada-titulo">
                  {planRequerido ? t('ficha.requiere_plan', { plan: planRequerido }) : t('t33.en_los_planes')}
                </Typography>
                <Typography className="cerrada-pie">
                  {t('ficha.ya_tienes_cuenta')} <RouterLink to="/entrar">{t('menu.entrar')}</RouterLink>
                </Typography>
              </Box>
            </Box>
          )}

          {puedeLeer && (
            <Box className={`cuerpo-canalizacion alin-${alineacion}`} sx={{ '--rayo': rayo, '--tam': `${tam}rem` }}>
              <PieceBlocks blocks={blocks} />
              {ensenanza.firma && (
                <Typography component="p" className="firma" sx={{ color: rayo, borderTopColor: 'var(--linea)' }}>
                  {ensenanza.firma}
                </Typography>
              )}
            </Box>
          )}

          <Box className="no-imprimir" sx={{
            mt: 4, pt: 3, borderTop: '1px solid var(--linea)',
            display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end'
          }}>
            {puedeLeer && (
              <Tooltip title={t('ficha.imprimir')} arrow>
                <Box component="button" type="button" aria-label={t('ficha.imprimir')} onClick={() => window.print()} sx={btnIcono}>
                  <PrintIcon fontSize="small" />
                </Box>
              </Tooltip>
            )}
            <Tooltip title={t('ficha.letra_chica')} arrow>
              <Box component="button" type="button" aria-label={t('ficha.letra_chica')} onClick={achicar} sx={btnIcono}><TextDecreaseIcon fontSize="small" /></Box>
            </Tooltip>
            <Tooltip title={t('ficha.letra_grande')} arrow>
              <Box component="button" type="button" aria-label={t('ficha.letra_grande')} onClick={agrandar} sx={btnIcono}><TextIncreaseIcon fontSize="small" /></Box>
            </Tooltip>
            <Tooltip title={esOscuro ? t('ficha.fondo_claro') : t('ficha.fondo_oscuro')} arrow>
              <Box component="button" type="button" aria-label={t('ficha.cambiar_fondo')} onClick={alternar} sx={btnIcono}>
                {esOscuro ? <LightIcon fontSize="small" /> : <DarkIcon fontSize="small" />}
              </Box>
            </Tooltip>
          </Box>

          <Box className="procedencia solo-imprimir">
            {ensenanza.id} · {numero}/33 · {formatoTitulo(ensenanza.titulo)} · {nombreVoz(entidad, t)}, {t('ficha.canalizado_por_min')} {ensenanza.canalizador} · jeshua.ong
          </Box>
        </>)}

        {/* SUS CANALIZACIONES (Def 93): las publicadas con este código, como
            fichas de la Biblioteca. El número se cuenta del catálogo. */}
        <Box className="no-imprimir" sx={{ mt: 6 }}>
          <Typography component="h2" sx={{
            fontFamily: 'var(--fuente-titulo)', fontSize: '.72rem', letterSpacing: '.18em',
            textTransform: 'uppercase', color: 'var(--texto-tenue)', mb: 1.5
          }}>
            {t('t33.p.canalizaciones')}
            <Box component="span" sx={{ color: 'var(--texto-tenue)', letterSpacing: 0, textTransform: 'none', ml: 1 }}>
              · {suyas.length === 1 ? t('t33.ens_1') : t('t33.ens_n', { n: suyas.length })}
            </Box>
          </Typography>

          <Box className="bib-lista t33-fichas">
            {suyas.map((c) => (
              <RouterLink key={c.id} to={`/canalizacion/${c.slug}`} className="bib-ficha t33-ficha"
                          style={{ '--rayo': colorIdentidad(c.entidad, modo) }}>
                <span className="bib-ficha-barra" />
                <span className="bib-ficha-cuerpo">
                  <span className="bib-ficha-titulo">{formatoTitulo(c.titulo)}</span>
                  <span className="bib-ficha-voz">
                    {nombreVoz(c.entidad, t)}
                    <span className="bib-ficha-quien"> · {c.canalizador}</span>
                  </span>
                  {c.extracto && <span className="bib-ficha-extracto">{c.extracto}…</span>}
                </span>
              </RouterLink>
            ))}
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button component={RouterLink} to={`/canalizaciones?ensenanza=${codigo}`}
                    sx={{ color: 'var(--texto-tenue)', fontSize: '.78rem', px: 0, '&:hover': { color: 'var(--texto)', bgcolor: 'transparent' } }}>
              {t('t33.p.biblioteca')}
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

const btnIcono = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  width: '2.4rem', height: '2.4rem', borderRadius: '2px',
  border: '1px solid var(--linea)', bgcolor: 'transparent', color: 'var(--texto-suave)',
  '&:hover': { color: 'var(--texto)' }
};

export default EnsenanzaPage;
